import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ICerere, ICerereEtapa } from '../../cereri.interface';
import { CereriService } from '../../cereri.service';
import { AppFilesService } from 'src/app/services/app-files.service';
import { helper } from 'src/environments/helper';
import Swal from 'sweetalert2';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
    selector: 'app-etape-decontare',
    templateUrl: './etape-decontare.component.html',
    styleUrls: ['./etape-decontare.component.scss'],
})
export class EtapeDecontareComponent {
    @Input() cerere!: ICerere;
    @Output() etapaUpdated = new EventEmitter<void>();

    helper = helper;

    constructor(
        private cereriService: CereriService,
        private appFileService: AppFilesService,
        public permissionsService: PermissionsService,
    ) { }

    // ── Program info helpers ──

    getProgramLabel(): string {
        const slug = this.cerere?.slug || '';
        const labels: Record<string, string> = {
            'ajutor-carburant': 'Ajutor de Carburant',
            'ajutor-transport-public': 'Ajutor Transport Public',
        };
        return labels[slug] || slug;
    }

    getProgramBadge(): string {
        const slug = this.cerere?.slug || '';
        return slug === 'ajutor-carburant' ? 'Carburant' : 'Transport';
    }

    getBadgeClass(): string {
        const slug = this.cerere?.slug || '';
        return slug === 'ajutor-carburant' ? 'badge-carburant' : 'badge-transport';
    }

    getTipTransportLabel(): string {
        const tip = this.cerere?.special_payload?.tip_transport;
        const labels: Record<string, string> = {
            'abonament_lunar': 'Abonament Lunar',
            'abonament_anual': 'Abonament Anual',
            'bilete': 'Bilete',
        };
        return labels[tip] || tip || '';
    }

    // ── Status display helpers ──

    isEtapaFullyCompleted(etapa: ICerereEtapa): boolean {
        return etapa.status === 'paid' && etapa.docs_status === 'approved';
    }

    isLocked(etapa: ICerereEtapa): boolean {
        return !etapa.unlocked;
    }

    // ── Calendar phase helpers (regula nouă 2026-06-17) ──

    isFutureEtapa(etapa: ICerereEtapa): boolean {
        // is_calendar_open vine din BE. Fallback pe perioada_start dacă lipsește.
        if (typeof etapa.is_calendar_open === 'boolean') return !etapa.is_calendar_open;
        if (!etapa.perioada_start) return false;
        return new Date(etapa.perioada_start) > new Date();
    }

    isPastEtapaWithoutDocs(etapa: ICerereEtapa): boolean {
        return !!etapa.is_past_period
            && etapa.docs_status === 'not_uploaded'
            && etapa.status !== 'paid';
    }

    formatPerioadaStartShort(etapa: ICerereEtapa): string {
        if (!etapa.perioada_start) return '';
        const d = new Date(etapa.perioada_start);
        return d.toLocaleDateString('ro-RO', { day: '2-digit', month: 'long', year: 'numeric' });
    }

    downloadDocument(docId: number): void {
        const fullPath = this.cereriService.downloadDocumentUrl + docId;
        this.appFileService.downloadFile(fullPath).catch(err => {
            console.error('File download error:', err);
        });
    }

    showDocsActions(etapa: ICerereEtapa): boolean {
        return etapa.can_process_docs;
    }

    showPaymentActions(etapa: ICerereEtapa): boolean {
        return etapa.status === 'ready_for_payment' || etapa.status === 'paid';
    }

    // ── Operator actions ──

    approveDocs(etapa: ICerereEtapa): void {
        Swal.fire({
            title: this.helper.swalTextConfig.commonTitles.areYouSure,
            text: `Aprobați documentele pentru Etapa ${etapa.nr}?`,
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: this.helper.swalTextConfig.validationMessages.confirmButton,
            cancelButtonText: this.helper.swalTextConfig.validationMessages.cancelButton,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            preConfirm: () => {
                return this.cereriService
                    .approveEtapaDocs(etapa.id)
                    .toPromise()
                    .then((response) => response)
                    .catch((err) => {
                        let msg;
                        try { msg = err.error.message || err.error.errors?.message?.[0]; }
                        catch (e) { msg = helper.dialogConfig.generalMessages.error; }
                        Swal.showValidationMessage(`Request failed: ${msg}`);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.helper.swalTextConfig.headers.success,
                    text: `Documentele etapei ${etapa.nr} au fost aprobate.`,
                    icon: 'success',
                    confirmButtonText: this.helper.swalTextConfig.buttonLabels.close,
                }).then(() => {
                    this.etapaUpdated.emit();
                });
            }
        });
    }

    rejectDocs(etapa: ICerereEtapa): void {
        // Determină automat ce înseamnă "respingere definitivă" pentru etapa curentă:
        //   - Etapa plătită -> recuperare sume reale
        //   - Etapa neplătită -> anulare etapă (fără sume de recuperat)
        const isPaid = etapa.status === 'paid';
        const definitiveLabel = isPaid
            ? 'Respinge definitiv — RECUPERARE SUME (plată confirmată)'
            : 'Respinge definitiv — ANULARE ETAPĂ (plată neefectuată)';
        const definitiveSubtext = isPaid
            ? 'Plata a fost confirmată — etapa intră în procedură de recuperare sume.'
            : 'Plata nu s-a făcut — etapa va fi anulată. Nu există sume de recuperat.';

        Swal.fire({
            title: `Respingere documente — Etapa ${etapa.nr}`,
            html:
                '<div style="text-align:left">' +
                '<label style="font-weight:600;display:block;margin-bottom:8px">Motivul respingerii *</label>' +
                '<textarea id="swal-motiv" class="swal2-textarea" placeholder="Descrieți motivul..." style="min-height:80px"></textarea>' +
                '<div style="margin-top:16px">' +
                '<label style="font-weight:600;display:block;margin-bottom:8px">Ce se întâmplă mai departe?</label>' +
                '<label style="display:flex;align-items:center;gap:8px;margin:8px 0;cursor:pointer">' +
                '<input type="radio" name="decision" value="reupload" checked>' +
                '<span>Permite re-încărcare (cetățeanul va fi notificat)</span>' +
                '</label>' +
                '<label style="display:flex;align-items:flex-start;gap:8px;margin:8px 0;cursor:pointer">' +
                '<input type="radio" name="decision" value="recovery" style="margin-top:4px">' +
                '<span style="color:#e74c3c">' +
                '<strong>' + definitiveLabel + '</strong>' +
                '<br><small style="color:#666;font-weight:normal">' + definitiveSubtext + '</small>' +
                '</span>' +
                '</label>' +
                '</div>' +
                '</div>',
            showCancelButton: true,
            confirmButtonText: 'Confirmă respingerea',
            cancelButtonText: 'Anulează',
            confirmButtonColor: '#e74c3c',
            reverseButtons: true,
            showLoaderOnConfirm: true,
            preConfirm: () => {
                const motivEl = document.getElementById('swal-motiv') as HTMLTextAreaElement;
                const motiv = motivEl?.value || '';
                if (!motiv.trim()) {
                    Swal.showValidationMessage('Motivul este obligatoriu!');
                    return false;
                }
                const decisionEl = document.querySelector('input[name="decision"]:checked') as HTMLInputElement;
                const allowReupload = decisionEl?.value === 'reupload';

                return this.cereriService
                    .rejectEtapaDocs(etapa.id, motiv, allowReupload)
                    .toPromise()
                    .then((response) => response)
                    .catch((err) => {
                        let msg;
                        try { msg = err.error.message || err.error.errors?.message?.[0]; }
                        catch (e) { msg = helper.dialogConfig.generalMessages.error; }
                        Swal.showValidationMessage(`Request failed: ${msg}`);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.helper.swalTextConfig.headers.success,
                    text: 'Respingerea a fost procesată.',
                    icon: 'success',
                    confirmButtonText: this.helper.swalTextConfig.buttonLabels.close,
                }).then(() => {
                    this.etapaUpdated.emit();
                });
            }
        });
    }

    unlockEtapa(etapa: ICerereEtapa): void {
        Swal.fire({
            title: this.helper.swalTextConfig.commonTitles.areYouSure,
            text: `Deblocați manual Etapa ${etapa.nr}?`,
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: this.helper.swalTextConfig.validationMessages.confirmButton,
            cancelButtonText: this.helper.swalTextConfig.validationMessages.cancelButton,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            preConfirm: () => {
                return this.cereriService
                    .unlockEtapa(etapa.id)
                    .toPromise()
                    .then((response) => response)
                    .catch((err) => {
                        let msg;
                        try { msg = err.error.message || err.error.errors?.message?.[0]; }
                        catch (e) { msg = helper.dialogConfig.generalMessages.error; }
                        Swal.showValidationMessage(`Request failed: ${msg}`);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.helper.swalTextConfig.headers.success,
                    text: `Etapa ${etapa.nr} a fost deblocată.`,
                    icon: 'success',
                    confirmButtonText: this.helper.swalTextConfig.buttonLabels.close,
                }).then(() => {
                    this.etapaUpdated.emit();
                });
            }
        });
    }

    confirmPayment(etapa: ICerereEtapa): void {
        const sumaPresetata = etapa.payment_amount || 0;
        Swal.fire({
            title: `Confirmare plată — Etapa ${etapa.nr}`,
            html:
                '<div style="text-align:left">' +
                '<label style="display:block;margin-bottom:4px;font-weight:600">Sumă (lei)</label>' +
                '<input id="swal-suma" class="swal2-input" type="number" value="' + sumaPresetata + '" readonly ' +
                'style="margin:0 0 4px 0;width:100%;background-color:#f5f5f5;cursor:not-allowed;font-weight:bold;font-size:16px;">' +
                '<p style="color:#666;font-size:12px;margin:0 0 12px;">Suma este pre-calculată și nu poate fi modificată.</p>' +
                '<label style="display:block;margin-bottom:4px;font-weight:600">Referință plată</label>' +
                '<input id="swal-ref" class="swal2-input" type="text" placeholder="OP-2026-001" style="margin:0;width:100%">' +
                '</div>',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: this.helper.swalTextConfig.validationMessages.confirmButton,
            cancelButtonText: this.helper.swalTextConfig.validationMessages.cancelButton,
            reverseButtons: true,
            showLoaderOnConfirm: true,
            preConfirm: () => {
                const refEl = document.getElementById('swal-ref') as HTMLInputElement;
                const suma = sumaPresetata;
                const referinta = refEl?.value || '';

                if (!suma || suma <= 0) {
                    Swal.showValidationMessage('Suma nu este setată pe etapă!');
                    return;
                }

                return this.cereriService
                    .confirmPayment(etapa.id, suma, referinta)
                    .toPromise()
                    .then((response) => response)
                    .catch((err) => {
                        let msg;
                        try { msg = err.error.message || err.error.errors?.message?.[0]; }
                        catch (e) { msg = helper.dialogConfig.generalMessages.error; }
                        Swal.showValidationMessage(`Request failed: ${msg}`);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.helper.swalTextConfig.headers.success,
                    text: `Plata pentru Etapa ${etapa.nr} a fost confirmată.`,
                    icon: 'success',
                    confirmButtonText: this.helper.swalTextConfig.buttonLabels.close,
                }).then(() => {
                    this.etapaUpdated.emit();
                });
            }
        });
    }
}
